import styled from 'styled-components';
import { lighten } from 'polished';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { LAYOUT, SPACING_SCALE } from '@/constants/styles';
import { CheckMark } from '@/components/uielements/icon/check-mark';
import { ExclamationTriangle } from '@/components/uielements/icon/exclamation-triangle';
import { ExclamationCircle } from '@/components/uielements/icon/exclamation-circle';

export const StyledReorderFormContent = styled.div`
  padding: ${LAYOUT.GUTTER}px;
  padding-top: 0px;
  display: flex;
  flex-direction: column;
`;

export const StyledReorderFormDate = styled.div(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '15px',
}));

export const StyledDate = styled.span(({ theme }) => ({
  color: theme.global.primary_active_color,
  fontSize: '13px',
  fontWeight: 800,
  lineHeight: '19.5px',
}));

export const StyledConveyanceBadge = styled.span(({ theme }) => ({
  padding: '1.5px 8px',
  borderRadius: '30px',
  backgroundColor: theme.global.primary_active_color,
  color: theme.buttons.primary_button_text_color ?? '#FFFFFF',
  fontSize: '10px',
  fontWeight: 800,
  textTransform: 'uppercase',
  lineHeight: '15px',
}));

export const StyledOrderNumber = styled.span(() => ({
  fontSize: '12px',
  lineHeight: '18px',
  opacity: 0.6,
}));

export const StyledLocationContainer = styled.div(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  marginBottom: '14px',
}));

export const StyledLocationLabel = styled.span(() => ({
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '18px',
  opacity: 0.6,
}));

export const StyledItemsSectionLabel = styled.span(() => ({
  fontSize: '11px',
  fontWeight: 800,
  textTransform: 'uppercase',
  lineHeight: '16.5px',
  margin: '0 0 var(--size-2)',
  marginBottom: '14px',
  textAlign: 'left',
  opacity: 0.6,
}));

export const StyledItemsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const StyledItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ theme }) => theme.global.secondary_body_color};
  border-radius: 12px;
  padding: 16px;
`;

export const StyledItemName = styled.span(({ theme }) => ({
  fontSize: '13px',
  color: theme.global.primary_active_color,
  fontWeight: 600,
  lineHeight: '19.5px',
}));

export const StyledItemQuantity = styled.span(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  padding: '3px 8.406px 3px 9px',
  borderRadius: '30px',
  border: `solid 1px ${theme.location_card.border_color ?? 'transparent'}`,
}));

export const StyledReorderFooter = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const StyledAddToCartButton = styled(StyledPrimaryButton)`
  width: 100%;
  font-size: 15px;
`;

export const StyledDeliveryContainer = styled.div(({ theme }) => ({
  padding: '17.5px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: '12px',
  border: `solid 1px ${theme.location_card.border_color ?? 'transparent'}`,
  borderRadius: '12px',
}));

export const StyledDeliveryLabelContainer = styled.div(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  alignItems: 'center',
}));

export const StyledDeliveryLabel = styled.span(() => ({
  fontSize: '11px',
  fontWeight: 800,
  lineHeight: '16.5px',
  textTransform: 'uppercase',
  opacity: 0.6,
}));

export const StyledChangeAddressButton = styled.button(({ theme }) => ({
  all: 'unset',
  outline: 'revert',
  color: theme.global.primary_active_color,
  fontWeight: 800,
  fontSize: '12px',
  lineHeight: '18px',
  cursor: 'pointer',

  '&:hover': {
    textDecoration: 'underline',
  },
}));

export const StyledDeliveryStatusText = styled.span(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 800,
  padding: '5px 9px',
  display: 'flex',
  gap: '3px',
  backgroundColor: lighten(0.68, theme.global.success_color),
  color: theme.global.success_color,
  border: `1px solid ${lighten(0.5, theme.global.success_color)}`,
  borderRadius: '12px',
  alignItems: 'center',
}));

export const StyledCheckMark = styled(CheckMark)`
  width: 17px;
  height: 17px;
`;

export const StyledDeliveryAddress = styled.span(({ theme }) => ({
  backgroundColor: theme.global.secondary_body_color,
  borderRadius: '10px',
  padding: '8px 12px',
  border: `solid 1px ${theme.location_card.border_color ?? 'transparent'}`,
  fontSize: '13px',
  width: '100%',
  textAlign: 'left',
  opacity: 0.6,
}));

export const StyledValidateAddress = styled.span(() => ({
  fontSize: '11px',
  fontWeight: 800,
  textAlign: 'center',
  width: '100%',
}));

export const StyledDeliveryError = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: '18.2px',
  padding: '12px',
  color: theme.global.error_color ?? '#B91C1C',
  backgroundColor: lighten(0.53, theme.global.error_color ?? '#B91C1C'),
  border: `solid 1px ${lighten(0.45, theme.global.error_color ?? '#B91C1C')}`,
  borderRadius: '10px',
  textAlign: 'left',
  gap: '8px',
}));

export const StyledExclamationTriangle = styled(ExclamationTriangle)`
  min-width: 15px;
  min-height: 15px;
`;

export const StyledGoogleAutocompleteWrapper = styled.div`
  width: 100%;
`;

export const StyledAddShippingDetails = styled.div`
  border: solid 1px ${({ theme }) => theme.global.primary_border_color};
  border-radius: ${SPACING_SCALE.THREEXS}px;
  padding: ${SPACING_SCALE.THREEXS}px;
  display: flex;
  gap: ${SPACING_SCALE.THREEXS}px;
  margin: 0.75rem 0 0;
  align-items: center;
`;

export const StyledExclamationCircle = styled(ExclamationCircle)`
  color: ${({ theme }) => theme.global.primary_border_color};
  width: 24px;
  height: 24px;
  margin-top: -4px;
`;

export const StyledAddShippingDetailsLabel = styled.span`
  font-size: 13px;
  line-height: 18px;
  text-align: left;
`;

export const StyledCancelButton = styled.button(() => ({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  width: '100%',
  fontSize: '13px',
  padding: '8px',
  opacity: 0.7,

  '&:hover': {
    opacity: 1,
    textDecoration: 'underline',
  },
}));
